import type { ReactNode } from 'react';
import { Box, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

import { PLAN_COLORS } from '../constants';
import type { Batch, PlanConfig, SegmentBreakdown } from '../types';
import { Pill } from './Pill';

interface LoadScheduleTabProps {
  batches: Batch[];
  config: PlanConfig;
  openBatches: Record<string, boolean>;
  toggleBatch: (key: string) => void;
}

const SectionHeader = ({
  children,
  mt,
}: {
  children: ReactNode;
  mt?: number;
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, mt }}>
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 700,
        color: 'text.secondary',
        letterSpacing: '0.07em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        display: 'flex',
        alignItems: 'center',
        gap: 1,
      }}
    >
      {children}
    </Typography>
    <Box sx={{ flex: 1, height: '1px', bgcolor: 'divider' }} />
  </Box>
);

const Breakdown = ({
  rows,
  title,
  bg,
}: {
  rows: SegmentBreakdown[];
  title: string;
  bg?: string;
}) => (
  <Box
    sx={{
      px: 1.5,
      py: 1,
      pl: 11.5,
      borderTop: '1px solid',
      borderColor: 'divider',
      bgcolor: bg ?? 'grey.50',
    }}
  >
    <Typography sx={{ fontSize: 10, color: 'text.secondary', mb: 0.625, fontWeight: 500 }}>
      {title}
    </Typography>
    {rows.map((s) => (
      <Box
        key={s.name}
        sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: 11, py: 0.25, fontFamily: 'monospace' }}
      >
        <Typography sx={{ fontSize: 11, color: 'text.secondary', minWidth: 120, fontFamily: 'monospace' }}>
          {s.name}
        </Typography>
        {s.past > 0 && (
          <Typography sx={{ fontSize: 11, color: PLAN_COLORS.past, fontWeight: 600, fontFamily: 'monospace' }}>
            past: {s.past.toLocaleString()}
          </Typography>
        )}
        {s.newL > 0 && (
          <Typography sx={{ fontSize: 11, color: PLAN_COLORS.newLeads, fontWeight: 600, fontFamily: 'monospace' }}>
            new: {s.newL.toLocaleString()}
          </Typography>
        )}
      </Box>
    ))}
  </Box>
);

export const LoadScheduleTab = ({
  batches,
  config: c,
  openBatches,
  toggleBatch,
}: LoadScheduleTabProps) => {
  const rampBatches = batches.filter((b) => b.isRamp);
  const others = batches.filter((b) => !b.isRamp);
  const transitions = others.filter((b) => b.isCadenceSwitch || b.isNewStart || b.isTransition);
  const steadyPhases = others.filter((b) => b.isSteady);
  const newDuringRamp = c.newStart <= c.ramp;

  return (
    <Box>
      <Box
        sx={{
          borderRadius: 1,
          p: 1.25,
          fontSize: 12,
          lineHeight: 1.5,
          mb: 1.75,
          borderLeft: `3px solid ${PLAN_COLORS.both}`,
          bgcolor: PLAN_COLORS.bothBg,
          color: PLAN_COLORS.both,
        }}
      >
        <strong>Lead window config:</strong> Past leads load from <strong>Day 1</strong> (lookback
        window). New unsold leads start from <strong>Day {c.newStart}</strong>{' '}
        {newDuringRamp
          ? `(during ramp-up — batches from day ${c.newStart} onward carry both sources).`
          : '(after ramp-up ends — new leads appear at the separate event below).'}
      </Box>

      <SectionHeader>
        Ramp-up batches
        <Pill color={PLAN_COLORS.orange} bg={PLAN_COLORS.orangeBg}>
          every {c.rampCad}d
        </Pill>
        <Typography
          component="span"
          sx={{
            fontWeight: 400,
            textTransform: 'none',
            letterSpacing: 0,
            fontSize: 11,
            color: 'text.secondary',
          }}
        >
          {rampBatches.length} batches over {c.ramp} days — click to expand
        </Typography>
      </SectionHeader>

      {rampBatches.map((b, i) => {
        const key = `r${i}`;
        const open = !!openBatches[key];
        return (
          <Box
            key={key}
            sx={{
              border: '1px solid',
              borderColor: b.batchNewActive ? PLAN_COLORS.both : 'divider',
              borderRadius: 1,
              bgcolor: 'background.paper',
              mb: 0.625,
              overflow: 'hidden',
            }}
          >
            <Box
              onClick={() => toggleBatch(key)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 1,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'grey.50' },
              }}
            >
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  fontFamily: 'monospace',
                  color: PLAN_COLORS.orange,
                  minWidth: 80,
                  flexShrink: 0,
                }}
              >
                {b.period}
              </Typography>
              {b.batchNewActive && (
                <Pill color={PLAN_COLORS.both} bg={PLAN_COLORS.bothBg} sx={{ fontSize: 9 }}>
                  +new
                </Pill>
              )}
              <Typography sx={{ flex: 1, fontSize: 11, color: 'text.secondary', lineHeight: 1.4 }}>
                {b.note}
              </Typography>
              <Typography
                sx={{
                  fontSize: 10,
                  color: 'text.disabled',
                  fontFamily: 'monospace',
                  minWidth: 90,
                  textAlign: 'right',
                  flexShrink: 0,
                }}
              >
                {b.hist}
              </Typography>
              <Pill color="text.secondary" bg="grey.200" sx={{ fontSize: 10 }}>
                {b.freq}
              </Pill>
              <Pill color={PLAN_COLORS.orange} bg={PLAN_COLORS.orangeBg} sx={{ fontSize: 10 }}>
                {b.totalLeads.toLocaleString()} leads
              </Pill>
              {b.emailDemandAtEnd != null && (
                <Pill color={PLAN_COLORS.past} bg={PLAN_COLORS.pastBg} sx={{ fontSize: 10 }}>
                  ~{b.emailDemandAtEnd.toLocaleString()} emails/d
                </Pill>
              )}
              {open ? (
                <ExpandLessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              )}
            </Box>
            {open && (
              <>
                {b.emailDemandAtEnd != null && (
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.75,
                      borderTop: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'grey.50',
                      fontSize: 11,
                      color: 'text.secondary',
                    }}
                  >
                    Email demand at end of window:{' '}
                    <strong>{b.emailDemandAtEnd.toLocaleString()}</strong> emails/day
                    {b.followUpAtEnd != null && (
                      <>
                        {' '}
                        (onboarding +{' '}
                        <strong>{b.followUpAtEnd.toLocaleString()}</strong> follow-ups)
                      </>
                    )}
                  </Box>
                )}
                <Breakdown
                  rows={b.breakdown}
                  title={
                    b.batchNewActive
                      ? 'Per-segment: past (blue) + new (green) leads'
                      : `Per-segment: past leads only (new leads start day ${c.newStart})`
                  }
                />
              </>
            )}
          </Box>
        );
      })}

      {transitions.map((b) => {
        const isNew = b.isNewStart;
        const color = isNew ? PLAN_COLORS.both : PLAN_COLORS.orange;
        const bg = isNew ? PLAN_COLORS.bothBg : PLAN_COLORS.orangeBg;
        const key = isNew ? 'newstart' : b.isCadenceSwitch ? 'cadsw' : 'trans';
        const open = !!openBatches[key];
        return (
          <Box
            key={key}
            sx={{
              border: `1px solid ${color}`,
              borderRadius: 1,
              bgcolor: bg,
              mt: 1,
              mb: 1,
              overflow: 'hidden',
            }}
          >
            <Box
              onClick={() => toggleBatch(key)}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, cursor: 'pointer' }}
            >
              <Typography
                sx={{ fontSize: 11, fontWeight: 600, fontFamily: 'monospace', color, minWidth: 80, flexShrink: 0 }}
              >
                {b.period}
              </Typography>
              <Typography sx={{ flex: 1, fontSize: 11, color, fontWeight: 500 }}>{b.note}</Typography>
              <Pill color={color} bg={bg} bordered sx={{ fontSize: 10 }}>
                {b.totalLeads.toLocaleString()} leads/batch
              </Pill>
              {open ? (
                <ExpandLessIcon sx={{ fontSize: 16, color }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: 16, color }} />
              )}
            </Box>
            {open && (
              <Breakdown
                rows={b.breakdown}
                title={isNew ? 'New leads window opens — per-segment breakdown' : 'Transition breakdown'}
                bg={bg}
              />
            )}
          </Box>
        );
      })}

      <SectionHeader mt={1.5}>
        Post-ramp & steady state
        <Pill color={PLAN_COLORS.green} bg={PLAN_COLORS.greenBg}>
          every {c.ssCad}d
        </Pill>
      </SectionHeader>

      {steadyPhases.map((b) => {
        const key = b.key ?? b.period;
        const open = !!openBatches[key];
        return (
          <Box
            key={key}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: 'background.paper',
              mb: 0.625,
              overflow: 'hidden',
            }}
          >
            <Box
              onClick={() => toggleBatch(key)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                px: 1.5,
                py: 1,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'grey.50' },
              }}
            >
              <Typography
                sx={{ fontSize: 11, fontWeight: 600, fontFamily: 'monospace', minWidth: 80, flexShrink: 0 }}
              >
                {b.period}
              </Typography>
              <Typography sx={{ flex: 1, fontSize: 11, color: 'text.secondary' }}>{b.note}</Typography>
              <Pill color="text.secondary" bg="grey.200" sx={{ fontSize: 10 }}>
                {b.freq}
              </Pill>
              <Pill color={PLAN_COLORS.green} bg={PLAN_COLORS.greenBg} sx={{ fontSize: 10 }}>
                {b.totalLeads.toLocaleString()} leads/batch
              </Pill>
              {open ? (
                <ExpandLessIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              )}
            </Box>
            {open && (
              <Breakdown rows={b.breakdown} title="Per-segment (new unsold leads only)" />
            )}
          </Box>
        );
      })}
    </Box>
  );
};
